import { useCallback, useSyncExternalStore, type ReactNode } from "react";
import type { TerminalAppModel } from "./state/ui-types";
import { ForestScene } from "./components/ForestScene";
import { getPresentationPolicy } from "./state/presentation-policy";
import { LeafIcon, RefreshIcon } from "./components/Icons";
import { MiningTerminal, useMiningPresentation } from "./features/mining";
import { MiningSessionStatus } from "./features/mining/components/MiningSessionStatus";
import { SessionGuidance } from "./components/SessionGuidance";
import { Toast, type ToastType } from "./components/Toast";
import { ToastSlot } from "./state/use-toast";
import { MintProgressBar, NetBuyPoolStrip, OddsDrawer, PoolDrawer, StatusDrawer, TierGuideDrawer } from "./features/dashboard";
import { MintHistory, NftBrowser, NftDetailSheet, InventoryHistoryDrawer, useNftPresentation,
  MintConfirmationDialog, TierSelectionDialog, useMintPresentation } from "./features/nft";
import { AppLayout } from "./components/AppLayout";
import { TransactionStatus } from "./components/TransactionStatus";
import { useWalletNotifications } from "./state/use-wallet-notifications";
import { useContextState } from "./state/use-context-state";
import { actionError } from "./lib/action-error";
import { walletSessionAction } from "./state/wallet-session-action";

export interface AppProps {
  model: TerminalAppModel;
  navigation?: ReactNode;
}

export function App({ model, navigation }: AppProps) {
  const invokeWallet = useWalletNotifications(model.contextKey, model.wallet.error);
  return <AppLayout model={model} navigation={navigation} onWalletAction={invokeWallet}
    onSelectSourceToken={(address) => invokeWallet(() => model.actions.selectSourceToken(address), "Source token could not be changed.")}>
    <TerminalContent model={model} invokeWallet={invokeWallet} />
  </AppLayout>;
}

export function TerminalContent({ model, invokeWallet }: {
  model: TerminalAppModel;
  invokeWallet: ReturnType<typeof useWalletNotifications>;
}) {
  const mobileLayout = useSyncExternalStore(subscribeMobileLayout, readMobileLayout, () => false);
  const [ui, setUi] = useContextState(model.contextKey, createUiState);
  const updateUi = useCallback((patch: Partial<ReturnType<typeof createUiState>>) =>
    setUi((current) => ({ ...current, ...patch })), [setUi]);
  const { statusOpen, poolOpen, oddsOpen, tiersOpen, notice } = ui;
  const mining = useMiningPresentation({ contextKey: model.contextKey, actions: model.actions },
    { invoke, clearNotice: () => updateUi({ notice: null }) });
  const demoMode = model.environment === "DEMO";
  const mint = useMintPresentation({
    contextKey: model.contextKey, demoMode, mining: { proofId: model.mining.proofId },
    netBuy: model.netBuy, mintFee: model.mintFee, rewardPolicy: model.rewardPolicy,
    proofEligibility: model.proofEligibility, mintReview: model.mintReview ?? null,
    transactions: model.transactions, actions: model.actions,
  }, { invoke, notify: (notice) => updateUi({ notice }) });
  const effectiveEffects = getPresentationPolicy({ effects: model.effects, mining: model.mining.phase === "MINING" }).effects;

  const closeStatus = useCallback(() => updateUi({ statusOpen: false }), [updateUi]);
  const closePool = useCallback(() => updateUi({ poolOpen: false }), [updateUi]);
  const closeOdds = useCallback(() => updateUi({ oddsOpen: false }), [updateUi]);
  const closeTiers = useCallback(() => updateUi({ tiersOpen: false }), [updateUi]);

  function invoke(action: () => void | Promise<void>, fallback: string): void {
    // React does not await event callbacks. Own both synchronous throws and
    // asynchronous rejection here, while starting the action immediately.
    try { Promise.resolve(action()).catch((error: unknown) => updateUi({ notice: { type: "error", message: actionError(error, fallback) } })); }
    catch (error) { updateUi({ notice: { type: "error", message: actionError(error, fallback) } }); }
  }

  const sessionAction = walletSessionAction(model.wallet);
  const connected = sessionAction === null;
  const nft = useNftPresentation({
    contextKey: model.contextKey, demoMode, inventory: model.inventory, wallet: model.wallet,
    nftRead: model.nftRead ?? null, chainId: model.runtime.deployment?.chainId ?? null,
    finalizePermission: model.runtime.permissions?.finalize, finalizeNotice: model.finalizeNotice ?? null,
    redeemReview: model.redeemReview ?? null, walletTransactionsEnabled: model.walletTransactionsEnabled,
    transactions: model.transactions, actions: model.actions,
  }, { invoke, notify: (notice) => updateUi({ notice }) });
  return (
    <>
        <main id="main-content" tabIndex={-1}>
          <NetBuyPoolStrip
            wallet={model.wallet}
            compact={mobileLayout}
            token={model.activeSourceToken}
            netBuy={model.netBuy}
            pool={model.rewardPool}
            onOpenStatus={() => updateUi({ statusOpen: true })}
            onOpenPool={() => updateUi({ poolOpen: true })}
          />

          <MintProgressBar state={model.mintFee} />

          <section className="workspace" aria-label="Forest mining workspace">
            {!mobileLayout ? <ForestScene key="forest" effects={effectiveEffects} miningPhase={model.mining.phase} environment={model.environmentLabel} /> : null}
            <MiningTerminal
              compact={mobileLayout}
              guidance={<SessionGuidance model={model} reviewingMint={mint.mintOpen} />}
              mining={model.mining}
              telemetrySource={model.miningTelemetry}
              connected={connected}
              sessionAction={sessionAction}
              walletBusy={model.wallet.status === "CONNECTING" || model.wallet.status === "SIGNING_IN"}
              publicMiningEnabled={model.runtime.publicMiningEnabled}
              {...mining}
              onChooseTier={mint.openTier}
              onConnect={() => {
                if (sessionAction?.kind === "connect" && model.actions.openWalletSelection) model.actions.openWalletSelection();
                else invokeWallet(sessionAction?.kind === "switch" ? model.actions.switchNetwork :
                  sessionAction?.kind === "retry" ? model.actions.refresh :
                  sessionAction?.kind === "check-session" ? model.actions.checkWalletSession ?? model.actions.refresh :
                  sessionAction?.kind === "logout" ? model.actions.retryWalletLogout ?? model.actions.disconnectWallet : model.actions.connectWallet,
                  "Wallet request could not be completed.");
              }}
            />
            {mobileLayout ? <ForestScene key="forest" effects={effectiveEffects} miningPhase={model.mining.phase} environment={model.environmentLabel} /> : null}
          </section>

          <div className="workspace-utility-bar" role="region" aria-label="Workspace status and actions">
            <div className="runtime-pulse">
              <span className={`status-led status-${model.runtime.backend.toLowerCase()}`} aria-hidden="true" />
              <span>BACKEND {model.runtime.backend === "OFFLINE" ? "UNAVAILABLE" : model.runtime.backend}</span>
              <span>PRICE {model.runtime.price}</span>
              <span>COVERAGE {model.runtime.tradeCoverage}</span>
            </div>
            <MiningSessionStatus mining={model.mining} sessionAction={sessionAction}
              publicMiningEnabled={model.runtime.publicMiningEnabled} settingsBusy={mining.settingsBusy}
              onCheckStatus={mining.onCheckStatus} onRetrySubmission={mining.onRetrySubmission}
              onNewSession={mining.onNewSession} onSavedProof={mining.onSavedProof} />
            <div>
              <button type="button" className="text-button" aria-haspopup="dialog" onClick={() => updateUi({ tiersOpen: true })}>NFT TIERS</button>
              <button type="button" className="text-button" onClick={() => updateUi({ oddsOpen: true })}>REWARD ODDS</button>
              <button type="button" className="text-button" onClick={() => updateUi({ statusOpen: true })}>SYSTEM STATUS</button>
              <button type="button" className="icon-button compact" aria-label="Refresh current data" onClick={() => invoke(model.actions.refresh, "Refresh could not be completed.")}><RefreshIcon /></button>
            </div>
          </div>

          <TransactionStatus key={`activity:${model.contextKey}`} transactions={model.transactions}
            onCheck={(id) => invoke(() => model.actions.checkTransaction(id), "Confirmation could not be checked.")}
            onDismiss={model.actions.dismissTransaction} />
          <MintHistory context={model.mintHistory ?? null} />
          <NftBrowser key={model.contextKey} {...nft.browserProps} />
        </main>

        <footer className="app-footer">
          <div><LeafIcon /><span>MERKLEWOOD Protocol Terminal</span></div>
          <p>Proof of work precedes tier selection. Rewards finalize on-chain. No yield is guaranteed.</p>
          <span>CHAIN {model.activeSourceToken?.chainId ?? "—"}</span>
        </footer>
      {notice ? (
        <ToastSlot><Toast type={notice.type} message={notice.message} onDismiss={() => updateUi({ notice: null })} /></ToastSlot>
      ) : null}

      <TierSelectionDialog {...mint.tierProps} />
      <MintConfirmationDialog {...mint.confirmationProps} />
      <NftDetailSheet key={model.contextKey} {...nft.detailProps} />
      <StatusDrawer open={statusOpen} netBuy={model.netBuy} runtime={model.runtime} onClose={closeStatus} />
      <PoolDrawer open={poolOpen} pool={model.rewardPool} token={model.activeSourceToken} onClose={closePool} />
      <OddsDrawer open={oddsOpen} policy={model.rewardPolicy} onClose={closeOdds} />
      <TierGuideDrawer open={tiersOpen} netBuy={model.netBuy} onClose={closeTiers} />
      <InventoryHistoryDrawer {...nft.historyProps} />
    </>
  );
}

function createUiState() {
  return {
    statusOpen: false, poolOpen: false, oddsOpen: false, tiersOpen: false,
    notice: null as { type: ToastType; message: string } | null,
  };
}

const mobileQuery = "(max-width: 720px)";
function readMobileLayout() { return window.matchMedia(mobileQuery).matches; }
function subscribeMobileLayout(onChange: () => void) {
  const query = window.matchMedia(mobileQuery);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
